import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ORG_ROLES } from "../lib/orgs";
import type { Org, OrgMember, OrgRole } from "../lib/orgs";
import type { GitConn } from "../lib/gitconn";
import { tx } from "../i18n";
import { Button } from "./Button";
import { Alert, EmptyState } from "./Card";
import { Field, Input, Select } from "./Field";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";
import type { ProjectName } from "./ProjectScope";
import "./Orgs.css";

/* ══ 관리 › 조직 부품 — 시안 docs/screens/pr-port.md §25 · 결정 docs/decisions/org.md ══
   카드 한 장에 단추는 「관리」(멤버가 아닌 사람 · GUEST · USER 는 「보기」) 하나다. 이름 · git 연결 · 프로젝트 ·
   멤버 · 삭제는 카드 아래 펼치는 판에 있다(MCP 토큰 화면과 같은 모양). 역할 배지는 아이콘 + 글자다 — 색만으로 안 가른다. */

const ROLE_ICON: Record<OrgRole, IconName> = { OWNER: "shieldCheck", USER: "user", GUEST: "search" };
const ROLE_TONE: Record<OrgRole, string> = { OWNER: " b-badge-info", USER: "", GUEST: " b-badge-warning" };

export function RoleBadge({ role }: { role: OrgRole | null }) {
  if (!role) return <span className="b-badge orgs-role">{tx("orgs.role.none")}</span>;
  return (
    <span className={`b-badge${ROLE_TONE[role]} orgs-role`}>
      <Icon name={ROLE_ICON[role]} />
      {role}
    </span>
  );
}

/** 역할 설명 표 — 원본(pr.b3x.app) 셋 그대로, 그리고 이 설치에서 무엇이 갈리는지. */
export function RoleGuide() {
  return (
    <div className="b-table-wrap">
      <table className="b-table orgs-guide" aria-label={tx("orgs.guide.title")}>
        <thead>
          <tr>
            <th>{tx("orgs.guide.role")}</th>
            <th>{tx("orgs.guide.can")}</th>
            <th>{tx("orgs.guide.cannot")}</th>
          </tr>
        </thead>
        <tbody>
          {ORG_ROLES.map((r) => (
            <tr key={r}>
              <td>
                <RoleBadge role={r} />
              </td>
              <td>{tx(`orgs.guide.${r}.can`)}</td>
              <td>{tx(`orgs.guide.${r}.cannot`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 조직 카드 — 이름 · 내 역할 · 프로젝트 n · 멤버 n · git 연결. 단추는 하나. */
export function OrgCard({ o, picked, isAdmin, onPick }: { o: Org; picked?: boolean; isAdmin?: boolean; onPick?: () => void }) {
  const manages = isAdmin || o.myRole === "OWNER";
  return (
    <section className={`b-card orgs-card${picked ? " is-picked" : ""}`}>
      <header className="orgs-card__head">
        <h3 className="b-card-title orgs-card__name">{o.name}</h3>
        <RoleBadge role={o.myRole} />
      </header>
      <dl className="orgs-card__facts">
        <div>
          <dt>{tx("orgs.card.projects")}</dt>
          <dd className="tnum">{o.projects.length}</dd>
        </div>
        <div>
          <dt>{tx("orgs.card.members")}</dt>
          <dd className="tnum">{o.memberCount}</dd>
        </div>
        <div className="orgs-card__git">
          <dt>{tx("orgs.card.git")}</dt>
          <dd>{o.gitConnection ? o.gitConnection.label : <span className="caption">{tx("orgs.git.none")}</span>}</dd>
        </div>
      </dl>
      <div className="orgs-card__acts">
        <Button size="sm" variant={picked ? "primary" : "ghost"} aria-expanded={picked} onClick={onPick}>
          {manages ? tx("orgs.card.manage") : tx("orgs.card.view")}
        </Button>
      </div>
    </section>
  );
}

export function OrgGrid({ children }: { children: React.ReactNode }) {
  return <div className="orgs-grid">{children}</div>;
}

export function NewOrgForm({ busy, error, onSubmit, onCancel }: { busy?: boolean; error?: string | null; onSubmit?: (name: string) => void; onCancel?: () => void }) {
  const [name, setName] = useState("");
  return (
    <section className="b-card orgs-new">
      <h3 className="b-card-title">{tx("orgs.new.title")}</h3>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <form
        className="orgs-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) onSubmit?.(name.trim());
        }}
      >
        <Field label={tx("orgs.form.name")} hint={tx("orgs.new.hint")}>
          {(id) => <Input id={id} autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} placeholder="irolab-co" required />}
        </Field>
        <div className="orgs-form__acts">
          <span className="orgs-gap" />
          <Button type="button" onClick={onCancel} disabled={busy}>
            {tx("common.action.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy} disabled={!name.trim()}>
            {tx("orgs.new.submit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** 멤버 표 — 역할 고르기(OWNER 만 바꾼다) + 「빼기」 하나. 빈 조직은 OWNER 를 먼저 세우라고 말한다. */
export function MemberTable({
  rows,
  canEdit,
  busy,
  onRole,
  onRemove,
}: {
  rows: OrgMember[];
  canEdit?: boolean;
  busy?: boolean;
  onRole?: (m: OrgMember, role: OrgRole) => void;
  onRemove?: (m: OrgMember) => void;
}) {
  const [armed, setArmed] = useState<string | null>(null);
  if (rows.length === 0) return <EmptyState compact icon="users" title={tx("orgs.members.empty")} desc={canEdit ? tx("orgs.members.emptyDesc") : undefined} />;
  return (
    <div className="b-table-wrap">
      <table className="b-table orgs-members" aria-label={tx("orgs.members.title")}>
        <thead>
          <tr>
            <th>{tx("orgs.members.user")}</th>
            <th>{tx("orgs.members.role")}</th>
            {canEdit ? <th>{tx("orgs.members.action")}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.userId}>
              <td className="is-primary">
                {m.username}
                {m.displayName ? <span className="caption"> · {m.displayName}</span> : null}
              </td>
              <td>
                {canEdit ? (
                  <Select size="sm" aria-label={tx("orgs.members.roleOf", { user: m.username })} value={m.role} disabled={busy} onChange={(e) => onRole?.(m, e.target.value as OrgRole)}>
                    {ORG_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <RoleBadge role={m.role} />
                )}
              </td>
              {canEdit ? (
                <td>
                  <Button
                    size="sm"
                    variant={armed === m.userId ? "danger" : "ghost"}
                    disabled={busy}
                    onClick={() => (armed === m.userId ? onRemove?.(m) : setArmed(m.userId))}
                    onBlur={() => setArmed(null)}
                  >
                    {armed === m.userId ? tx("orgs.members.removeConfirm") : tx("orgs.members.remove")}
                  </Button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 멤버 더하기 — 아이디 + 역할. 이미 멤버면 역할이 바뀐다(서버가 한 문으로 받는다). */
export function AddMemberForm({ busy, onSubmit }: { busy?: boolean; onSubmit?: (username: string, role: OrgRole) => void }) {
  const [user, setUser] = useState("");
  const [role, setRole] = useState<OrgRole>("USER");
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!user.trim()) return;
    onSubmit?.(user.trim(), role);
    setUser("");
  }
  return (
    <form className="orgs-addwrap" onSubmit={submit}>
      <div className="orgs-add">
      <Field label={tx("orgs.members.addUser")}>
        {(id) => <Input id={id} size="md" autoComplete="off" spellCheck={false} value={user} onChange={(e) => setUser(e.target.value)} />}
      </Field>
      <Field label={tx("orgs.members.role")}>
        {(id) => (
          <Select id={id} size="md" value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
            {ORG_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Button type="submit" size="md" loading={busy} disabled={!user.trim()}>
        {tx("orgs.members.add")}
      </Button>
      </div>
      <p className="caption">{tx("orgs.members.addHint")}</p>
    </form>
  );
}

/** 프로젝트 고르기 — 체크 목록. 다른 조직에 든 프로젝트는 그 이름을 같이 적는다(옮겨 온다는 것을 미리 말한다). */
function ProjectPick({
  value,
  projects,
  orgId,
  orgOf,
  onChange,
}: {
  value: string[];
  projects: (ProjectName & { orgId: string | null })[];
  orgId: string;
  orgOf: (id: string) => string;
  onChange: (v: string[]) => void;
}) {
  if (projects.length === 0) return <p className="caption">{tx("orgs.projects.noProjects")}</p>;
  return (
    <div className="orgs-pick">
      {projects.map((p) => (
        <label key={p.id} className="b-check">
          <input type="checkbox" checked={value.includes(p.id)} onChange={(e) => onChange(e.target.checked ? [...value, p.id] : value.filter((x) => x !== p.id))} />
          {p.name}
          {p.orgId && p.orgId !== orgId ? <span className="caption"> · {tx("orgs.projects.inOther", { org: orgOf(p.orgId) })}</span> : null}
        </label>
      ))}
    </div>
  );
}

/** 「관리」로 펼치는 판. 무엇을 그릴지는 역할이 정한다: 관리자 = 전부, OWNER = 이름 · 멤버, 그 밖 = 읽기만. */
export function OrgManage({
  o,
  members,
  isAdmin,
  projects,
  conns,
  busy,
  error,
  orgName,
  armed: armedInit = false,
  onRename,
  onGit,
  onProjects,
  onSetMember,
  onRemoveMember,
  onDelete,
  onClose,
}: {
  o: Org;
  members: OrgMember[] | null;
  isAdmin?: boolean;
  /** 관리자에게만 온다 — 프로젝트 배정이 관리자 문이다. */
  projects?: (ProjectName & { orgId: string | null })[];
  /** 관리자에게만 온다 — git 연결 목록이 `/admin/` 아래다. */
  conns?: Pick<GitConn, "id" | "label" | "provider">[];
  busy?: boolean;
  error?: string | null;
  orgName?: (id: string) => string;
  armed?: boolean;
  onRename?: (name: string) => void;
  onGit?: (connId: string | null) => void;
  onProjects?: (ids: string[]) => void;
  onSetMember?: (username: string, role: OrgRole) => void;
  onRemoveMember?: (m: OrgMember) => void;
  onDelete?: () => void;
  onClose?: () => void;
}) {
  const owner = isAdmin || o.myRole === "OWNER";
  const [name, setName] = useState(o.name);
  const [pick, setPick] = useState(o.projects.map((p) => p.id));
  const [armed, setArmed] = useState(armedInit);
  useEffect(() => {
    setName(o.name);
    setPick(o.projects.map((p) => p.id));
  }, [o]);
  const pickChanged = JSON.stringify([...pick].sort()) !== JSON.stringify(o.projects.map((p) => p.id).sort());
  return (
    <section className="b-card orgs-manage" aria-label={tx("orgs.manage.title", { name: o.name })}>
      <header className="b-card-head">
        <h3 className="b-card-title">{tx("orgs.manage.title", { name: o.name })}</h3>
        <div className="card__meta">
          <RoleBadge role={o.myRole} />
          {isAdmin && !o.myRole ? <span className="caption">{tx("orgs.manage.adminNote")}</span> : null}
        </div>
      </header>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {!owner ? <Alert tone="info">{tx(o.myRole === "GUEST" ? "orgs.manage.guestNote" : "orgs.manage.userNote")}</Alert> : null}

      <div className="orgs-manage__grid">
        <div className="orgs-sec">
          <h4 className="orgs-sec__title">{tx("orgs.manage.basic")}</h4>
          {owner ? (
            <form
              className="orgs-inline"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim() && name.trim() !== o.name) onRename?.(name.trim());
              }}
            >
              <Field label={tx("orgs.form.name")}>{(id) => <Input id={id} size="md" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
              <Button type="submit" size="md" disabled={busy || !name.trim() || name.trim() === o.name}>
                {tx("common.action.save")}
              </Button>
            </form>
          ) : (
            <p className="orgs-kv">
              <span className="caption">{tx("orgs.form.name")}</span> {o.name}
            </p>
          )}
          {isAdmin && conns ? (
            <Field label={tx("orgs.card.git")} hint={tx("orgs.git.hint")}>
              {(id) => (
                <Select id={id} size="md" value={o.gitConnection?.id ?? ""} disabled={busy} onChange={(e) => onGit?.(e.target.value || null)}>
                  <option value="">{tx("orgs.git.none")}</option>
                  {conns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label} · {c.provider}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : (
            <p className="orgs-kv">
              <span className="caption">{tx("orgs.card.git")}</span> {o.gitConnection ? `${o.gitConnection.label} · ${o.gitConnection.provider}` : tx("orgs.git.none")}
            </p>
          )}
        </div>

        <div className="orgs-sec">
          <h4 className="orgs-sec__title">{tx("orgs.projects.title", { n: o.projects.length })}</h4>
          <p className="caption">{tx("orgs.projects.lead")}</p>
          {isAdmin && projects ? (
            <>
              <ProjectPick value={pick} projects={projects} orgId={o.id} orgOf={orgName ?? ((id) => id)} onChange={setPick} />
              <div className="orgs-form__acts">
                <span className="orgs-gap" />
                <Button size="md" disabled={busy || !pickChanged} onClick={() => onProjects?.(pick)}>
                  {tx("orgs.projects.save")}
                </Button>
              </div>
            </>
          ) : o.projects.length ? (
            <ul className="orgs-plist">
              {o.projects.map((p) => (
                <li key={p.id}>{p.name}</li>
              ))}
            </ul>
          ) : (
            <p className="caption">{tx("orgs.projects.empty")}</p>
          )}
        </div>
      </div>

      <div className="orgs-sec">
        <h4 className="orgs-sec__title">{tx("orgs.members.title")}</h4>
        {members === null ? <p className="caption">{tx("orgs.members.loading")}</p> : <MemberTable rows={members} canEdit={owner} busy={busy} onRole={(m, r) => onSetMember?.(m.username, r)} onRemove={onRemoveMember} />}
        {owner ? <AddMemberForm busy={busy} onSubmit={onSetMember} /> : null}
      </div>

      <div className="orgs-form__acts orgs-manage__foot">
        {isAdmin ? (
          <Button variant={armed ? "danger" : "ghost"} disabled={busy} onClick={() => (armed ? onDelete?.() : setArmed(true))} onBlur={() => setArmed(false)}>
            {armed ? tx("orgs.deleteConfirm") : tx("common.action.delete")}
          </Button>
        ) : null}
        <span className="orgs-gap" />
        <Button onClick={onClose} disabled={busy}>
          {tx("common.action.close")}
        </Button>
      </div>
      {armed ? <p className="caption">{tx("orgs.deleteNote")}</p> : null}
    </section>
  );
}
